import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Field, reduxForm, getFormValues } from "redux-form";
import cx from "classnames";
import ModalVideo from "modules/modal/modal-video";

import CheckboxGroupField from "modules/forms/fields/checkbox-group-field";
import BodyModelField from "modules/forms/fields/body-model-field";

import {
  QuestionTypes,
  QuestionSubTypes,
  QuestionKeys
} from "modules/diagnostic/diagnostic-constants";

import {
  getAnswerGlowing,
  isHumanModelQuestion,
  isVASQuestion
} from "modules/diagnostic/diagnostic-selectors";

import PainVAS from "modules/forms/fields/pain-vas";
import RangeField from "modules/forms/fields/range-field";
import { sanitizeHTMLString } from "modules/utils/text-utils";
import { getLocaleSpecificProp } from "modules/i18n/locale-utils";
import TextField from "../forms/fields/text-field";
import { getBrand } from "../brand/brand-selectors";

class QuestionForm extends Component {
  static propTypes = {
    question: PropTypes.object,
    answerGlowing: PropTypes.bool,
    hasPrevious: PropTypes.bool,
    hasValues: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    previous: PropTypes.string,
    className: PropTypes.string,
    next: PropTypes.string,
    direction: PropTypes.bool,
    videoURL: PropTypes.string,
    onNext: PropTypes.func.isRequired,
    onPrevious: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    brand: PropTypes.object.isRequired,
    isVASQuestion: PropTypes.bool,
    isHumanModelQuestion: PropTypes.bool
  };

  state = {
    showPlayer: false
  };

  showPlayer = () => this.setState({ showPlayer: true });
  hidePlayer = () => this.setState({ showPlayer: false });

  transformAnswers = answers =>
    (answers &&
      Object.keys(answers).map(key => ({
        value: key,
        label: getLocaleSpecificProp(answers[key], this.props.locale)
      }))) ||
    [];

  renderRadioButtons = ({ values }) => (
    <div className="Question-answers">
      <ul className="Question-answers-list">
        {this.transformAnswers(values).map((val, i) => (
          <li key={i} className={cx({ glow: this.props.answerGlowing })}>
            <Field
              id={`answer-${val.value}`}
              type="radio"
              name="answer"
              component="input"
              value={val.value}
            />{" "}
            <label htmlFor={`answer-${val.value}`} key={val.value}>
              <span>{val.label}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );

  renderSingleOptions = () => {
    const {
      question: { answer, question, key },
      locale
    } = this.props;

    if (key === QuestionKeys.PAIN_VAS) {
      return (
        <PainVAS
          question={question}
          name="answer"
          min={answer.min}
          max={answer.max}
        />
      );
    }

    switch (answer.subtype) {
      case QuestionSubTypes.RANGE:
        return (
          <RangeField
            name="answer"
            min={answer.min}
            max={answer.max}
            label={getLocaleSpecificProp(question, locale)}
            initialValue={0}
          />
        );
      case QuestionSubTypes.FREE_TEXT:
        return (
          <TextField
            className="Question-answers QuestionForm__FreeText"
            name="answer"
            textarea
          />
        );
      default:
        return this.renderRadioButtons(answer);
    }
  };

  renderMultipleOptions = () => {
    const {
      question: {
        question,
        answer: { values },
        key
      }
    } = this.props;
    if (key === QuestionKeys.HUMAN_MODEL) {
      return <BodyModelField name="answer" question={question} />;
    }
    return (
      <div className="Question-answers">
        <ul className="Question-answers-list">
          <CheckboxGroupField
            name="answer"
            options={this.transformAnswers(values)}
          />
        </ul>
      </div>
    );
  };

  onResize = () => {
    this.setState({ height: `${window.innerHeight}px` });
  };

  onNext = () => {
    this.props.onNext();
  };

  onPrev = () => {
    this.props.onPrevious();
  };

  componentDidMount() {
    this.setState({ height: `${window.innerHeight}px` });
    window.addEventListener("resize", this.onResize);
  }

  componentWillUnmount() {
    if (this.timeout) {
      window.clearTimeout(this.timeout);
    }
    window.removeEventListener("resize", this.onResize);
  }

  getBackgroundStyles() {
    const {
      brand,
      question: { key }
    } = this.props;

    const breakpoint = 768;
    const backgroundColor = brand.secondaryBackground.backgroundColor;
    const color1 = brand.secondaryBackground.gradientColors[0];
    const color2 = brand.secondaryBackground.gradientColors[1];

    if (key === QuestionKeys.PAIN_VAS || key === QuestionKeys.HUMAN_MODEL) {
      return `
      .QuestionForm {
        background: ${backgroundColor}
      }`;
    }

    return `
      .QuestionForm {
        background-image: linear-gradient(180deg, ${color1} 0%, ${color1} 50%, ${color2} 50%, ${color2} 100%)
      }
      
      @media(min-width: ${breakpoint}px) {
        .QuestionForm {
          background-image: linear-gradient(90deg, ${color1} 0%, ${color1} 50%, ${color2} 50%, ${color2} 100%)
        }
      }
    `;
  }

  render() {
    if (!this.props.question) {
      return null;
    }

    const {
      className,
      hasPrevious,
      previous,
      next,
      question: {
        question,
        answer: { type }
      },
      locale,
      hasValues,
      videoURL,
      brand,
      isVASQuestion,
      isHumanModelQuestion
    } = this.props;
    const classNames = cx("QuestionForm", className, {
      "QuestionForm--VAS": isVASQuestion,
      "QuestionForm--human": isHumanModelQuestion
    });

    const logo = brand.imageQA;

    return (
      <form className={classNames}>
        <style
          dangerouslySetInnerHTML={{ __html: this.getBackgroundStyles() }}
        />
        {logo && (
          <img className="QuestionForm__Logo" src={logo} alt="Company logo" />
        )}
        <button
          className="Button--prevQuestion"
          type="button"
          disabled={!hasPrevious}
          onClick={this.onPrev}
        />
        <div
          className="QuestionForm-question"
          style={{ "--inner-height": this.state.height }}
        >
          <div className="Question-text">
            <div
              className="Question--prev"
              onClick={this.onPrev}
              dangerouslySetInnerHTML={{
                __html: sanitizeHTMLString(previous)
              }}
            />
            <div
              className={cx("Question--current", {
                "Question--video": videoURL
              })}
            >
              {videoURL && (
                <button
                  className="Button--play"
                  type="button"
                  onClick={this.showPlayer}
                />
              )}
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTMLString(
                    getLocaleSpecificProp(question, locale)
                  )
                }}
              />
            </div>
            <div
              className="Question--next"
              onClick={this.onNext}
              dangerouslySetInnerHTML={{
                __html: sanitizeHTMLString(next)
              }}
            />
          </div>
          {type === QuestionTypes.MULTIPLE
            ? this.renderMultipleOptions()
            : this.renderSingleOptions()}
        </div>
        <button
          className={cx({
            "Button--nextQuestion": true,
            "is-hidden": !hasValues
          })}
          type="button"
          onClick={this.onNext}
        />
        {videoURL &&
          this.state.showPlayer && (
            <ModalVideo url={videoURL} close={this.hidePlayer} />
          )}
      </form>
    );
  }
}

export default reduxForm()(
  connect((state, { form }) => {
    const formValues = getFormValues(form)(state);
    const hasValues = Boolean(
      formValues &&
        formValues.answer &&
        (typeof formValues.answer !== "object" ||
          (typeof formValues.answer === "object" &&
            formValues.answer.length > 0))
    );
    return {
      hasValues,
      answerGlowing: getAnswerGlowing(state),
      brand: getBrand(state),
      isVASQuestion: isVASQuestion(state),
      isHumanModelQuestion: isHumanModelQuestion(state)
    };
  })(QuestionForm)
);



// WEBPACK FOOTER //
// ./src/modules/general-info/question-form.js